/**
 * *(Owner, 2026-10-12)* The browser's own error and diagnostic report — `client.report`.
 *
 * Some failures happen only inside one student's phone or browser: a PDF that will not open on an
 * older iPhone, a text reader that shows "1 / 1" on one Safari. The server's logs see only the
 * operations that reached them, so on iPhone, Android and desktop alike this module captures the
 * page's uncaught errors and unhandled promise rejections, plus the few diagnostics product code
 * reports through `reportDiagnostic`, and sends them in small batches to `client.report`, which writes
 * them to Cloud Logging beside the operation records. Nothing is stored in the database.
 *
 * The rules, and why each one exists
 * ----------------------------------
 *  - **It never throws and never retries.** A reporter that can break the page, or that keeps a loop
 *    alive against a failing network, is worse than no reporter. A failed send is dropped.
 *  - **It never reports itself.** Everything the reporter does runs inside one re-entrancy guard, so
 *    an error raised while capturing or sending is not captured, and a failed send cannot become an
 *    `unhandledrejection` because its promise is always handled.
 *  - **It is bounded.** Identical messages are reported once, at most twenty events leave one page
 *    load, and a batch is at most ten — the contract's own bound. Every field is cut to the bound the
 *    shared contract freezes (`CLIENT_REPORT_LIMITS`), so a long stack can never make a whole batch
 *    fail validation.
 *  - **It sends only for a signed-in session.** `client.report` needs an Account, so the queue waits
 *    for a session; a page that never signs in simply unloads with it.
 *  - **It carries no personal data.** The route is the path only — never the query or fragment, which
 *    can carry tokens — and the server scrubs e-mail-shaped and long-digit text again before logging.
 *
 * The core (`createClientReporter`) takes every capability as a port, which is what lets its
 * batching, deduplication, cap and failure rules be tested with fake timers and a fake transport.
 */
import {
  CLIENT_REPORT_DETAIL_KEY_PATTERN,
  CLIENT_REPORT_LIMITS,
  type ClientReportKind,
} from '@simple-qa/shared';

import { subscribeToSession } from '../auth/session';
import { installSignInTrace } from '../auth/signInTrace';
import { callOperation, type OperationRequest } from './api';
import { currentEnv, firebaseConfigStatus } from './firebase';

export type ClientReportRequest = OperationRequest<'client.report'>;
export type ClientInfo = ClientReportRequest['client'];
type ClientEvent = ClientReportRequest['events'][number];

/** What product code may attach to a diagnostic: measurements, never objects. */
export type DiagnosticDetails = Readonly<Record<string, string | number | boolean | null | undefined>>;

/** At most this many events leave one page load. */
export const MAX_EVENTS_PER_PAGE = 20;

/** A batch is sent this long after its first event, so a burst of errors becomes one call. */
export const FLUSH_DELAY_MS = 3_000;

export interface ClientReporterPorts {
  /** Sends one batch. May reject; the reporter drops the batch. */
  readonly send: (request: ClientReportRequest) => Promise<unknown>;
  /** Starts observing the session. Called once, on the first captured event. */
  readonly watchSession: (onChange: (signedIn: boolean) => void) => void;
  readonly clientInfo: () => ClientInfo;
  /** The current route path. */
  readonly where: () => string;
  readonly now: () => Date;
}

export interface ClientReporter {
  /** Queue one event. Total: whatever it is handed, it never throws. */
  readonly capture: (kind: ClientReportKind, message: unknown, details?: DiagnosticDetails) => void;
  /** Send what is queued now, if a session is signed in. */
  readonly flush: () => void;
}

function text(value: unknown, max: number): string {
  try {
    return (typeof value === 'string' ? value : String(value)).slice(0, max);
  } catch {
    // `String()` throws for an object with no usable conversion; that is not worth a report.
    return '';
  }
}

function detailsOf(details: DiagnosticDetails | undefined): ClientEvent['details'] {
  const out: Record<string, string | number | boolean | null> = {};
  if (details === undefined || details === null || typeof details !== 'object') return out;
  let kept = 0;
  for (const [key, value] of Object.entries(details)) {
    if (kept >= CLIENT_REPORT_LIMITS.detailKeys) break;
    if (!CLIENT_REPORT_DETAIL_KEY_PATTERN.test(key) || value === undefined) continue;
    if (typeof value === 'number') out[key] = Number.isFinite(value) ? value : null;
    else if (typeof value === 'string') out[key] = value.slice(0, CLIENT_REPORT_LIMITS.detailText);
    else if (typeof value === 'boolean' || value === null) out[key] = value;
    else continue;
    kept += 1;
  }
  return out;
}

/** The batching, deduplicating, capped core. Every capability comes in as a port. */
export function createClientReporter(ports: ClientReporterPorts): ClientReporter {
  const queue: ClientEvent[] = [];
  const seen = new Set<string>();
  let accepted = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let signedIn = false;
  let watching = false;
  // The re-entrancy guard: nothing the reporter does while it is already working is captured.
  let busy = false;

  function clearTimer(): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }

  function schedule(): void {
    if (timer === null) timer = setTimeout(flush, FLUSH_DELAY_MS);
  }

  function watch(): void {
    if (watching) return;
    watching = true;
    ports.watchSession((next) => {
      signedIn = next;
      if (next && queue.length > 0) schedule();
    });
  }

  function flush(): void {
    clearTimer();
    if (!signedIn || queue.length === 0) return;
    const wasBusy = busy;
    busy = true;
    try {
      const client = ports.clientInfo();
      while (queue.length > 0) {
        const events = queue.splice(0, CLIENT_REPORT_LIMITS.eventsPerReport);
        // Handled here, always: a failed send is dropped and can never surface as a rejection.
        ports.send({ client, events }).catch(() => undefined);
      }
    } catch {
      // A send that throws synchronously is dropped like one that rejects.
      queue.length = 0;
    } finally {
      busy = wasBusy;
    }
  }

  function capture(kind: ClientReportKind, message: unknown, details?: DiagnosticDetails): void {
    if (busy) return;
    busy = true;
    try {
      const body = text(message, CLIENT_REPORT_LIMITS.message);
      const key = `${kind}\u0000${body}`;
      if (seen.has(key) || accepted >= MAX_EVENTS_PER_PAGE) return;
      seen.add(key);
      accepted += 1;
      queue.push({
        kind,
        message: body,
        where: text(ports.where(), CLIENT_REPORT_LIMITS.where),
        at: ports.now().toISOString(),
        details: detailsOf(details),
      });
      watch();
      if (queue.length >= CLIENT_REPORT_LIMITS.eventsPerReport) {
        flush();
      } else {
        schedule();
      }
    } catch {
      // Reporting is best effort; a reporter failure is never a page failure.
    } finally {
      busy = false;
    }
  }

  return Object.freeze({ capture, flush });
}

/* ------------------------------------------------------------------ *
 * Reading the browser
 * ------------------------------------------------------------------ */

function bounded(value: number, max: number, fallback: number): number {
  return Number.isFinite(value) && value >= 0 ? Math.min(Math.round(value), max) : fallback;
}

interface NavigatorExtras {
  readonly userAgentData?: { readonly platform?: unknown };
  readonly standalone?: unknown;
}

/**
 * The device as the page sees it. `standalone` is the installed-app mode — `display-mode: standalone`
 * everywhere, and the older `navigator.standalone` on iOS Safari.
 */
export function readClientInfo(appVersion: string): ClientInfo {
  const nav = navigator as Navigator & NavigatorExtras;
  const uaPlatform = nav.userAgentData?.platform;
  const platform = typeof uaPlatform === 'string' && uaPlatform.length > 0 ? uaPlatform : nav.platform;
  const dpr = window.devicePixelRatio;
  let standalone = nav.standalone === true;
  try {
    standalone = standalone || window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    // An environment without `matchMedia` is simply not standalone.
  }
  return {
    userAgent: text(nav.userAgent, CLIENT_REPORT_LIMITS.userAgent),
    platform: text(platform ?? '', CLIENT_REPORT_LIMITS.platform),
    language: text(nav.language ?? '', CLIENT_REPORT_LIMITS.language),
    viewport: {
      w: bounded(window.innerWidth, CLIENT_REPORT_LIMITS.viewport, 0),
      h: bounded(window.innerHeight, CLIENT_REPORT_LIMITS.viewport, 0),
    },
    dpr: Number.isFinite(dpr) && dpr > 0 ? Math.min(dpr, CLIENT_REPORT_LIMITS.devicePixelRatio) : 1,
    standalone,
    appVersion: text(appVersion, CLIENT_REPORT_LIMITS.appVersion),
  };
}

/** An error's name and message — the part that identifies it, without the whole object. */
export function describeError(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  return text(value, CLIENT_REPORT_LIMITS.message);
}

/** The first lines of a stack, which is usually where the failure is. */
function stackOf(value: unknown): string | undefined {
  if (!(value instanceof Error) || typeof value.stack !== 'string') return undefined;
  return value.stack.slice(0, CLIENT_REPORT_LIMITS.detailText);
}

/**
 * Wires a reporter to the page's `error`, `unhandledrejection` and `pagehide` events, and returns the
 * function that unwires it again.
 */
export function attachToWindow(reporter: ClientReporter, target: Window = window): () => void {
  const onError = (event: ErrorEvent) => {
    reporter.capture('error', event.message || describeError(event.error), {
      source: event.filename,
      line: event.lineno,
      column: event.colno,
      stack: stackOf(event.error),
    });
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    reporter.capture('unhandledrejection', describeError(event.reason), { stack: stackOf(event.reason) });
  };
  // The last chance to send what is queued; a page that unloads signed out drops it.
  const onPageHide = () => reporter.flush();
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  target.addEventListener('pagehide', onPageHide);
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
    target.removeEventListener('pagehide', onPageHide);
  };
}

/* ------------------------------------------------------------------ *
 * The application's reporter
 * ------------------------------------------------------------------ */

let installed: ClientReporter | null = null;

/** The build's identity: the Vite mode, as the app has no separate build id. */
function appVersion(): string {
  const mode = currentEnv()['MODE'];
  return typeof mode === 'string' ? mode : '';
}

/**
 * Installs the one reporter of this page. Called once from `main.tsx`; a second call is a no-op, and
 * it never throws — a page without a working reporter is still a working page.
 */
export function installClientReporter(): void {
  if (installed !== null) return;
  try {
    const version = appVersion();
    const reporter = createClientReporter({
      send: (request) => callOperation('client.report', request),
      watchSession: (onChange) => {
        // An unconfigured build has no session and no backend; its reports are never sent.
        if (!firebaseConfigStatus().configured) return;
        subscribeToSession((state) => onChange(state.status === 'signedIn'));
      },
      clientInfo: () => readClientInfo(version),
      where: () => window.location.pathname,
      now: () => new Date(),
    });
    attachToWindow(reporter);
    installed = reporter;
    // *(Owner, 2026-10-16)* The sign-in trace sends through the same operation, waiting for its own
    // signed-in moment (see `signInTrace.ts`).
    installSignInTrace(
      (events) =>
        callOperation('client.report', {
          client: readClientInfo(version),
          events: events.map((event) => ({
            kind: 'other' as const,
            message: event.message,
            where: text(window.location.pathname, CLIENT_REPORT_LIMITS.where),
            at: new Date().toISOString(),
            details: event.details,
          })),
        }),
      () => readClientInfo(version).standalone,
    );
  } catch {
    installed = null;
  }
}

/**
 * Report one diagnostic from product code — a PDF that failed to open, a reader's pagination
 * measurements. A no-op until `installClientReporter` has run (in tests, for instance), and it never
 * throws.
 */
export function reportDiagnostic(kind: ClientReportKind, message: unknown, details?: DiagnosticDetails): void {
  installed?.capture(kind, message, details);
}
